import React, { useEffect, useState } from "react";
import { SettingsAPI, ClinicExportJob } from "../../../services/settings.service";
import { CloseIcon } from "../../../components/icons";
import { DownloadIcon, WarningTriangleIcon } from "../../../components/icons/exportIcons";

interface RequestExportModalProps {
  clinicName: string;
  latestManifest: Record<string, number> | null;
  onClose: () => void;
  onStarted: (job: ClinicExportJob) => void;
}

const TABLE_ROWS: { key: string; label: string }[] = [
  { key: "patients", label: "patients" },
  { key: "appointments", label: "appointments" },
  { key: "visits", label: "visits" },
  { key: "invoices", label: "invoices" },
  { key: "soap_notes", label: "soap_notes" },
  { key: "doctors", label: "doctors" },
  { key: "vitals", label: "vitals" },
  { key: "clinic_documents", label: "document metadata" },
  { key: "prescriptions", label: "prescriptions" },
];

export const RequestExportModal: React.FC<RequestExportModalProps> = ({
  clinicName,
  latestManifest,
  onClose,
  onStarted,
}) => {
  const [requesterName, setRequesterName] = useState("");
  const [requesterEmail, setRequesterEmail] = useState("");
  const [includeDocuments, setIncludeDocuments] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    SettingsAPI.getCurrentUser()
      .then(u => {
        setRequesterName(`${u.first_name} ${u.last_name}`.trim());
        setRequesterEmail(u.email);
      })
      .catch(() => {});
  }, []);

  const totalRows = latestManifest
    ? Object.values(latestManifest).reduce((sum, n) => sum + n, 0)
    : null;

  const handleStart = async () => {
    setSubmitting(true);
    setError(null);
    try {
      const job = await SettingsAPI.requestClinicExport(includeDocuments);
      onStarted(job);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Could not start the export. Please try again.");
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="w-full max-w-2xl max-h-[90vh] overflow-y-auto rounded-2xl bg-white shadow-xl">
        <div className="flex items-start justify-between px-6 pt-6 pb-4">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">Request Data Export</h3>
            <p className="text-xs text-gray-500 mt-0.5">
              {clinicName || "Your clinic"} · requested by {requesterName || "you"}
            </p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <CloseIcon />
          </button>
        </div>

        <div className="px-6 space-y-5">
          <div>
            <div className="flex items-center justify-between mb-2">
              <h4 className="text-sm font-semibold text-gray-900">Included Records</h4>
              <span className="text-xs text-gray-500">
                9 of 9 tables{totalRows !== null ? ` · ${totalRows.toLocaleString()} rows` : ""}
              </span>
            </div>
            <div className="grid grid-cols-2 gap-x-6 gap-y-2 rounded-xl border border-gray-100 p-4">
              {TABLE_ROWS.map(row => (
                <label key={row.key} className="flex items-center justify-between text-sm text-gray-700 py-0.5">
                  <span className="flex items-center gap-2">
                    <input type="checkbox" checked disabled className="rounded border-gray-300 text-blue-600" />
                    {row.label}
                  </span>
                  <span className="text-gray-400">
                    {latestManifest?.[row.key]?.toLocaleString() ?? "—"}
                  </span>
                </label>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Date Range</label>
              <select disabled className="w-full rounded-lg border border-gray-300 bg-gray-50 px-3 py-2 text-sm text-gray-500">
                <option>All time</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">File Format</label>
              <select disabled className="w-full rounded-lg border border-gray-300 bg-gray-50 px-3 py-2 text-sm text-gray-500">
                <option>CSV</option>
              </select>
            </div>
          </div>

          <label className="flex items-center justify-between rounded-xl border border-gray-100 px-4 py-3">
            <span className="flex items-center gap-2 text-sm text-gray-700">
              <input
                type="checkbox"
                checked={includeDocuments}
                onChange={e => setIncludeDocuments(e.target.checked)}
                className="rounded border-gray-300 text-blue-600"
              />
              Include original documents (prescriptions, invoices, lab reports)
            </span>
          </label>

          <div>
            <h4 className="text-sm font-semibold text-gray-900 mb-1">Not Included</h4>
            <p className="text-xs text-gray-500">
              Payment gateway credentials (account id and status only) · aadhaar numbers (masked
              in every file) · records belonging to other clinics · internal audit logs.
            </p>
          </div>

          <div className="flex gap-3 rounded-xl border border-amber-200 bg-amber-50 p-4">
            <WarningTriangleIcon className="text-amber-600 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-semibold text-amber-800">
                Preparing this archive takes up to 40 minutes.
              </p>
              <p className="text-sm text-amber-700 mt-0.5">
                You can close this window. Kaagazy emails {requesterEmail || "you"} when the
                download is ready, and the link stays valid for 48 hours.
              </p>
            </div>
          </div>

          {error && (
            <p className="text-sm text-red-600 bg-red-50 border border-red-100 rounded-lg px-4 py-3">
              {error}
            </p>
          )}
        </div>

        <div className="flex items-center justify-between px-6 py-5 mt-2">
          <p className="text-xs text-gray-400">This request is audit-logged.</p>
          <div className="flex items-center gap-3">
            <button
              onClick={onClose}
              className="rounded-lg border border-gray-300 bg-white px-4 py-2.5 text-sm font-medium text-gray-700 hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              onClick={handleStart}
              disabled={submitting}
              className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2.5 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-60"
            >
              <DownloadIcon /> {submitting ? "Starting…" : "Start Export"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
